/**
 * ReviewCard
 * Displays a single review with user info, rating, and comment
 */

import { Avatar, AvatarFallback } from '@shared/components/ui/avatar'
import { Button } from '@shared/components/ui/button'
import { Card } from '@shared/components/ui/card'
import { StarRating } from '@shared/components/ui/star-rating'
import { getInitials } from '@shared/lib/utils'
import type { JobReview } from '@shared/types'
import { formatDistanceToNow } from 'date-fns'
import { es } from 'date-fns/locale'
import { Calendar, Trash2 } from 'lucide-react'

// ============================================================================
// Types
// ============================================================================

interface ReviewCardProps {
  review: JobReview
  canEdit?: boolean // If current user can delete this review
  onDelete?: (reviewId: string) => void
}

function formatDate(dateString: string): string {
  try {
    const date = new Date(dateString)
    return formatDistanceToNow(date, { addSuffix: true, locale: es })
  } catch {
    return dateString
  }
}

// ============================================================================
// Component
// ============================================================================

export function ReviewCard({ review, canEdit = false, onDelete }: ReviewCardProps) {
  const userName = review.user
    ? `${review.user.firstName} ${review.user.lastName}`
    : 'Usuario'
  const initials = getInitials(userName)

  return (
    <Card className="p-6">
      <div className="space-y-4">
        {/* Header: User Info + Actions */}
        <div className="flex items-start justify-between">
          {/* User Info */}
          <div className="flex items-start gap-4">
            <Avatar className="h-12 w-12">
              <AvatarFallback>{initials}</AvatarFallback>
            </Avatar>
            <div>
              <p className="font-semibold">{userName}</p>
              <p className="text-sm text-muted-foreground">
                {formatDate(review.createdAt)}
              </p>
            </div>
          </div>

          {/* Delete Button (for own reviews) */}
          {canEdit && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onDelete?.(review.id)}
              className="text-destructive hover:text-destructive hover:bg-destructive/10"
              title="Eliminar reseña"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          )}
        </div>

        {/* Rating */}
        <div>
          <StarRating rating={review.rating} showValue={false} />
        </div>

        {/* Comment */}
        {review.comment && (
          <p className="text-foreground leading-relaxed">{review.comment}</p>
        )}

        {/* Job Date */}
        {review.jobDate && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Calendar className="h-4 w-4" />
            <span>Trabajo realizado: {new Date(review.jobDate).toLocaleDateString('es-ES')}</span>
          </div>
        )}
      </div>
    </Card>
  )
}

// ============================================================================
// Loading Skeleton
// ============================================================================

export function ReviewCardSkeleton() {
  return (
    <Card className="p-6">
      <div className="space-y-4 animate-pulse">
        {/* Header */}
        <div className="flex items-start gap-4">
          <div className="h-12 w-12 bg-muted rounded-full" />
          <div className="flex-1 space-y-2">
            <div className="h-5 bg-muted rounded w-1/3" />
            <div className="h-4 bg-muted rounded w-1/4" />
          </div>
        </div>

        {/* Rating */}
        <div className="h-4 bg-muted rounded w-1/4" />

        {/* Comment */}
        <div className="space-y-2">
          <div className="h-4 bg-muted rounded w-full" />
          <div className="h-4 bg-muted rounded w-5/6" />
          <div className="h-4 bg-muted rounded w-4/6" />
        </div>
      </div>
    </Card>
  )
}
